import { useT } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContentContext";

/**
 * StockBadge — shows a small urgency chip when inventory is low.
 *
 * Rules (evaluated in order):
 *   1. One-of-a-kind piece (exactly 1 variant, no colour, no size, stock ≥ 1)
 *        → "Peça única" / "Unique piece" / "Pieza única"
 *   2. Total stock across all in-stock variants === 1
 *        → "Última peça" / "Only 1 left" / "Última pieza"
 *   3. Total stock 2 or 3
 *        → "Só restam N" / "Only N left" / "Solo quedan N"
 *   4. Otherwise → renders nothing
 *
 * We deliberately ignore fully out-of-stock variants (stock 0) when summing
 * so a product with 1 in stock + 5 sold-out variants still says "Only 1 left".
 */
const LOW_STOCK_THRESHOLD_DEFAULT = 3;

export const computeStockBadge = (product, threshold = LOW_STOCK_THRESHOLD_DEFAULT) => {
  const variants = product?.variants || [];
  if (variants.length === 0) return null;

  const inStock = variants.filter((v) => (Number(v?.stock) || 0) > 0);
  // Fully out of stock — surface a clear "Sold out" badge everywhere the
  // product appears (home, collection cards). The waitlist form on the
  // PDP still handles the "notify me" flow.
  if (inStock.length === 0) return { kind: "sold-out" };

  // Low-stock urgency. Total = sum of stock across every in-stock variant
  // (0-stock variants are already filtered out above). Threshold is
  // admin-configurable via /admin → Content → Scarcity indicators.
  const total = inStock.reduce((acc, v) => acc + (Number(v?.stock) || 0), 0);
  if (total === 1) return { kind: "one", n: 1 };
  if (total <= Math.max(1, Number(threshold) || LOW_STOCK_THRESHOLD_DEFAULT)) return { kind: "few", n: total };
  return null;
};

/**
 * Compact chip for use on collection cards and PDPs.
 * `size="sm"` (default) — collection cards; `size="md"` — PDP.
 */
const StockBadge = ({ product, size = "sm", className = "", ...rest }) => {
  const { t } = useT();
  const { settings } = useSiteContent();
  const badge = computeStockBadge(product, settings?.stock_left_threshold);
  if (!badge) return null;
  // Admin master switch — when off, hide the low-stock urgency chips
  // ("Only 1 left", "Only N left") but keep the sold-out chip because
  // that's factual information shoppers need, not urgency.
  if ((badge.kind === "one" || badge.kind === "few") && settings?.show_stock_left === false) return null;
  // Per-product override — admin can hide scarcity on a specific piece
  // without touching the sitewide switch. Product-level default is
  // True, so legacy products without the field keep showing scarcity.
  if ((badge.kind === "one" || badge.kind === "few") && product?.show_scarcity === false) return null;

  const label =
    badge.kind === "sold-out"
      ? t("waitlist.oosLabel")
      : badge.kind === "one"
      ? t("badge.onlyOneLeft")
      : t("badge.onlyNLeft", { n: badge.n });

  const sizeClass =
    size === "md"
      ? "text-[11px] px-3 py-1.5 tracking-[0.22em]"
      : "text-[9px] px-2.5 py-1 tracking-[0.2em]";

  // Sold-out uses a muted palette so it reads as "unavailable" rather than
  // "hot pick" like the low-stock urgency badges.
  const paletteClass =
    badge.kind === "sold-out"
      ? "bg-[#2C2C2C]/85 text-[#F1ECE2] backdrop-blur-sm"
      : "bg-[#241C18] text-[var(--gl-orange)] shadow-sm";

  return (
    <span
      data-testid={`stock-badge-${badge.kind}`}
      className={`inline-flex items-center gap-1 rounded-full font-badge uppercase ${paletteClass} ${sizeClass} ${className}`}
      {...rest}
    >
      {badge.kind !== "sold-out" && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--gl-orange)] animate-pulse" />
      )}
      {label}
    </span>
  );
};

export default StockBadge;
